import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

// Icons
import { BookOpen, Plus, Edit, Trash2, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubjectManagement = ({ user }) => {
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    coefficient: 1.0,
    teacher_ids: []
  });

  useEffect(() => {
    fetchSubjects();
    fetchTeachers();
  }, []);

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API}/subjects`);
      setSubjects(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des matières');
      console.error('Error fetching subjects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API}/users?role=TEACHER`);
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingSubject) {
        await axios.put(`${API}/subjects/${editingSubject.id}`, formData);
        toast.success('Matière mise à jour avec succès');
      } else {
        await axios.post(`${API}/subjects`, formData);
        toast.success('Matière créée avec succès');
      }
      
      resetForm();
      fetchSubjects();
      setDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error('Error saving subject:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      coefficient: 1.0,
      teacher_ids: []
    });
    setEditingSubject(null);
  };

  const handleEdit = (subject) => {
    setFormData({
      name: subject.name,
      code: subject.code,
      description: subject.description || '',
      coefficient: subject.coefficient,
      teacher_ids: subject.teacher_ids || []
    });
    setEditingSubject(subject);
    setDialogOpen(true);
  };

  const handleDelete = async (subjectId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette matière ?')) {
      try {
        await axios.delete(`${API}/subjects/${subjectId}`);
        toast.success('Matière supprimée avec succès');
        fetchSubjects();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
        console.error('Error deleting subject:', error);
      }
    }
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? `${teacher.first_name} ${teacher.last_name}` : 'Enseignant inconnu';
  };

  if (loading && subjects.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in" data-testid="subject-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <BookOpen className="w-6 h-6 mr-2 text-blue-600" />
            Gestion des Matières
          </h1>
          <p className="text-gray-600 mt-1">
            Gérez les matières enseignées dans l'établissement
          </p>
        </div>
        
        {(user.role === 'ADMIN' || user.role === 'PRINCIPAL') && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-subject-btn"
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Matière
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingSubject ? 'Modifier la matière' : 'Nouvelle matière'}
                </DialogTitle>
                <DialogDescription>
                  {editingSubject ? 'Modifiez les informations de la matière' : 'Ajoutez une nouvelle matière à l\'établissement'}
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom de la matière</Label>
                    <Input
                      id="name"
                      data-testid="subject-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="ex: Mathématiques"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="code">Code</Label>
                    <Input
                      id="code"
                      data-testid="subject-code-input"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="ex: MATH"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="coefficient">Coefficient</Label>
                  <Input
                    id="coefficient"
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="10"
                    value={formData.coefficient}
                    onChange={(e) => setFormData({ ...formData, coefficient: parseFloat(e.target.value) })}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description (optionnelle)</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description de la matière..."
                    rows={3}
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setDialogOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? 'Sauvegarde...' : (editingSubject ? 'Modifier' : 'Créer')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Matières</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{subjects.length}</div>
            <p className="text-xs text-gray-600 mt-1">
              Matières enseignées
            </p>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Enseignants Actifs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{teachers.length}</div>
            <p className="text-xs text-gray-600 mt-1">
              Professeurs disponibles
            </p>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Coefficient Moyen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {subjects.length > 0 ? (subjects.reduce((sum, s) => sum + s.coefficient, 0) / subjects.length).toFixed(1) : '0'}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Pondération moyenne
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Subjects List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {subjects.map((subject) => (
          <Card key={subject.id} className="card">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg font-semibold">{subject.name}</CardTitle>
                  <Badge className="badge-primary mt-1">{subject.code}</Badge>
                </div>
                
                {(user.role === 'ADMIN' || user.role === 'PRINCIPAL') && (
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(subject)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(subject.id)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Coefficient:</span>
                  <span className="font-medium">{subject.coefficient}</span>
                </div>
                
                {subject.description && (
                  <div>
                    <span className="text-sm text-gray-600">Description:</span>
                    <p className="text-sm mt-1">{subject.description}</p>
                  </div>
                )}
                
                {subject.teacher_ids && subject.teacher_ids.length > 0 && (
                  <div>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <Users className="w-4 h-4 mr-1" />
                      Enseignants assignés:
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {subject.teacher_ids.map((teacherId) => (
                        <Badge key={teacherId} className="badge-success text-xs">
                          {getTeacherName(teacherId)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {subjects.length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune matière</h3>
          <p className="text-gray-600 mb-6">
            Commencez par créer les matières enseignées dans votre établissement.
          </p>
          {(user.role === 'ADMIN' || user.role === 'PRINCIPAL') && (
            <Button 
              onClick={() => { resetForm(); setDialogOpen(true); }}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer la première matière
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default SubjectManagement;